import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers';
import { LIST_SINGLECAM_AGENT_POOL_QUERY_KEY } from '@/state/queries/singlecam-agent-admin/use-list-singlecam-agent-pool';
import { PLATFORM_SUMMARY_QUERY_KEY } from '@/state/queries/ops-admin/use-platform-summary';
import { ATTENTION_QUEUE_QUERY_KEY } from '@/state/queries/ops-admin/use-attention-queue';

function invalidateSingleCamQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: [LIST_SINGLECAM_AGENT_POOL_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: [PLATFORM_SUMMARY_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: [ATTENTION_QUEUE_QUERY_KEY] });
}

export function useCreateAzureSingleCamAgentMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (name?: string) => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/singlecam-agent/players/azure`;
      return sendJSONRequest({
        url,
        method: METHOD.POST,
        body: name ? { name } : {},
        token: token ?? '',
      }) as Promise<{ playerId: string; vmId: string }>;
    },
    onSuccess: () => invalidateSingleCamQueries(queryClient),
  });
}

export function useCreateStandaloneSingleCamAgentMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (name?: string) => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/singlecam-agent/players/standalone`;
      return sendJSONRequest({
        url,
        method: METHOD.POST,
        body: name ? { name } : {},
        token: token ?? '',
      }) as Promise<{ playerId: string; vmId: string }>;
    },
    onSuccess: () => invalidateSingleCamQueries(queryClient),
  });
}

export function useSingleCamAgentMaintenanceMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (playerId: string) => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/singlecam-agent/players/${encodeURIComponent(playerId)}/maintenance`;
      return sendJSONRequest({
        url,
        method: METHOD.POST,
        body: {},
        token: token ?? '',
      }) as Promise<{ playerId: string; opsStatus: string }>;
    },
    onSuccess: () => invalidateSingleCamQueries(queryClient),
  });
}

export function useSingleCamAgentPauseMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (playerId: string) => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/singlecam-agent/players/${encodeURIComponent(playerId)}/pause`;
      return sendJSONRequest({
        url,
        method: METHOD.POST,
        body: {},
        token: token ?? '',
      }) as Promise<{ playerId: string; opsStatus: string }>;
    },
    onSuccess: () => invalidateSingleCamQueries(queryClient),
  });
}

export function useSingleCamAgentResumeMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (playerId: string) => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/singlecam-agent/players/${encodeURIComponent(playerId)}/resume`;
      return sendJSONRequest({
        url,
        method: METHOD.POST,
        body: {},
        token: token ?? '',
      }) as Promise<{ playerId: string; opsStatus: string }>;
    },
    onSuccess: () => invalidateSingleCamQueries(queryClient),
  });
}

export function useDeleteSingleCamAgentMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (playerId: string) => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/singlecam-agent/players/${encodeURIComponent(playerId)}`;
      return sendJSONRequest({
        url,
        method: METHOD.DELETE,
        body: {},
        token: token ?? '',
      }) as Promise<{ playerId: string }>;
    },
    onSuccess: () => invalidateSingleCamQueries(queryClient),
  });
}
